import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Progress,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import moment from 'moment';
import DownloadIcon from 'mdi-react/DownloadIcon';
import fileDownload from 'js-file-download';
import { Parser } from 'json2csv';
import Loading from '../../../shared/components/custom/Loading';
import Api from '../../../util/api';
import { formatCurrencyNumber } from '../../../util/functions';
import { formatDateTime } from '../../../util/date-and-time';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import exportToExcelFile from '../../../util/xlsx';

const TYPES = ['raised', 'supporters', 'pricePerScore', 'scores'];
// Money types are stored in minor units (öre) and edited in kr
const MONEY_TYPES = ['raised', 'pricePerScore'];
const SCORE_TYPE_LABELS = {
  goal: 'scoreTypeGoal',
  point: 'scoreTypePoint',
  attendant: 'scoreTypeAttendant',
  holeInOne: 'scoreTypeHoleInOne',
  medal: 'scoreTypeMedal',
};

const toInput = (milestone) => ({
  ...milestone,
  value: MONEY_TYPES.includes(milestone.type)
    ? milestone.value / 100
    : milestone.value,
  description: milestone.description || '',
});
const toPayload = (row) => ({
  ...(row.id && { id: row.id }),
  type: row.type,
  value: MONEY_TYPES.includes(row.type)
    ? Math.round(Number(row.value) * 100)
    : Math.round(Number(row.value)),
  title: row.title.trim(),
  description: row.description.trim() || null,
});

// Stages shown with progress bars on the plan: money raised (counted as the
// fundraising goal's bar, so it can use the same amount), active supporters,
// what they pledge together per score, and scores made. Plan managers can edit
// them on the website too. When one is reached, the supporters active at that
// moment are saved and can be exported here, e.g. to offer them a special price.
const PlanMilestonesPage = ({ planId }) => {
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(null);
  const [scoreType, setScoreType] = useState('goal');
  const [loading, setLoading] = useState(true);
  const config = useConfig();
  const p = partial('goalSponsor');
  const s = partial('shared');
  const scoreLabel = p(SCORE_TYPE_LABELS[scoreType] || 'scoreTypeGoal');

  const apply = (plan) => {
    setRows((plan.milestones || []).map(toInput));
    setProgress(plan.progress || null);
  };

  const fetchMilestones = async () => {
    setLoading(true);
    try {
      const plan =
        await Api.goalSponsor.subscriptionPlans.getSubscriptionPlanById(planId);
      setScoreType(plan.scoreType || 'goal');
      apply(plan);
    } catch (err) {
      toast.error(p('milestonesRetrievingERROR'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMilestones();
  }, [planId]);

  const update = (index, field, value) =>
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );

  const isValid = rows.every(
    (row) => row.title.trim() && Number(row.value) > 0
  );

  const save = async () => {
    setLoading(true);
    try {
      apply(
        await Api.goalSponsor.subscriptionPlans.replaceMilestones(
          planId,
          rows.map(toPayload)
        )
      );
      toast.success(p('milestonesSaved'));
    } catch (err) {
      toast.error(
        `${err}`.includes('Duplicate')
          ? p('milestonesDuplicate')
          : p('milestonesSaveERROR')
      );
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = (type) =>
    ({
      raised: p('milestoneTypeRaised'),
      supporters: p('milestoneTypeSupporters'),
      pricePerScore: p('milestoneTypePricePerScore', { score: scoreLabel }),
      scores: p('milestoneTypeScores', { score: scoreLabel }),
    })[type];

  const current = (row) => {
    const value = progress?.[row.type] ?? 0;
    return MONEY_TYPES.includes(row.type) ? value / 100 : value;
  };

  const money = (minor) =>
    `${formatCurrencyNumber(Number(minor) / 100)} ${config.currencyCaps || ''}`.trim();

  // Rows keyed by their display label, as the subscription export
  const buildContributorRows = async (milestone) => {
    const contributors =
      await Api.goalSponsor.subscriptionPlans.getMilestoneContributors(
        planId,
        milestone.id
      );
    const companyIds = contributors
      .filter((row) => row.companyId)
      .map((row) => row.companyId);
    const companies =
      companyIds.length > 0
        ? await Api.companies.getCompaniesByIds(companyIds)
        : [];
    return contributors.map((row) => {
      const companyName = row.companyId
        ? companies
            .find((company) => company.id === row.companyId)
            ?.clubRelations.find(
              (relation) => relation.clubId == config.ntb_team_id
            )?.name
        : null;
      return {
        [s('name')]: companyName || row.userName || '',
        [s('email')]: row.userEmail || '',
        [p('contributorType')]: row.companyId
          ? p('contributorCompany')
          : p('contributorPrivate'),
        [p('contributorAmountPerScore', { score: scoreLabel })]: money(
          row.amountPerGoal
        ),
        [p('contributorSince')]: row.subscribedAt
          ? formatDateTime(row.subscribedAt)
          : '',
        [p('totalAmountCharged')]: money(row.totalChargedOre),
        [p('milestoneReachedAt')]: row.reachedAt
          ? formatDateTime(row.reachedAt)
          : '',
      };
    });
  };

  const exportContributors = async (milestone, format) => {
    setLoading(true);
    try {
      const rows = await buildContributorRows(milestone);
      if (!rows.length) {
        toast.info(p('noContributors'));
        return;
      }
      const fileName = `milestone-contributors-${milestone.id}`;
      if (format === 'csv') {
        fileDownload(`\uFEFF${new Parser().parse(rows)}`, `${fileName}.csv`);
      } else {
        exportToExcelFile(rows, fileName);
      }
    } catch (err) {
      toast.error(p('contributorsExportERROR'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Loading loading={loading} />
      <Row>
        <Col lg={10}>
          <p>{p('milestonesInfo')}</p>
          {progress && (
            <p className="text-muted">
              {p('milestonesCurrent', {
                raised: money(progress.raised || 0),
                supporters: progress.supporters,
                pricePerScore:
                  `${progress.pricePerScore / 100} ${config.currencyCaps || ''}`.trim(),
                scores: progress.scores,
              })}
            </p>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          {rows.length === 0 ? (
            <p className="text-muted">{p('noMilestones')}</p>
          ) : (
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>{p('milestoneType')}</th>
                  <th>{p('milestoneValue')}</th>
                  <th>{p('milestoneTitle')}</th>
                  <th>{p('milestoneDescription')}</th>
                  <th>{p('milestoneProgress')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id || `new-${index}`}>
                    <td style={{ minWidth: 180 }}>
                      <Input
                        type="select"
                        value={row.type}
                        onChange={(e) => update(index, 'type', e.target.value)}
                      >
                        {TYPES.map((type) => (
                          <option key={type} value={type}>
                            {typeLabel(type)}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td style={{ minWidth: 110 }}>
                      <Input
                        type="number"
                        min={1}
                        value={row.value}
                        onChange={(e) => update(index, 'value', e.target.value)}
                      />
                    </td>
                    <td style={{ minWidth: 180 }}>
                      <Input
                        maxLength={100}
                        value={row.title}
                        onChange={(e) => update(index, 'title', e.target.value)}
                      />
                    </td>
                    <td style={{ minWidth: 200 }}>
                      <Input
                        maxLength={300}
                        value={row.description}
                        onChange={(e) =>
                          update(index, 'description', e.target.value)
                        }
                      />
                    </td>
                    <td style={{ minWidth: 140 }}>
                      {row.reachedAt ? (
                        <Badge color="success">
                          {p('milestoneReached', {
                            date: moment(row.reachedAt).format('L'),
                          })}
                        </Badge>
                      ) : (
                        Number(row.value) > 0 && (
                          <>
                            <Progress
                              value={Math.min(
                                100,
                                (current(row) / Number(row.value)) * 100
                              )}
                            />
                            <small>
                              {current(row)} / {row.value}
                            </small>
                          </>
                        )
                      )}
                    </td>
                    <td className="text-right text-nowrap">
                      {row.id && row.reachedAt && (
                        <UncontrolledDropdown className="d-inline-block mr-2">
                          <DropdownToggle
                            size="sm"
                            outline
                            className="icon icon--right mb-0"
                          >
                            <DownloadIcon /> {p('exportContributors')}
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem
                              onClick={() => exportContributors(row, 'xlsx')}
                            >
                              {s('export')} (XLSX)
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => exportContributors(row, 'csv')}
                            >
                              {s('export')} (CSV)
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      )}
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        className="mb-0"
                        onClick={() =>
                          setRows((all) => all.filter((_, i) => i !== index))
                        }
                      >
                        {s('remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="d-flex">
            <Button
              size="sm"
              outline
              disabled={rows.length >= 20}
              onClick={() =>
                setRows((all) => [
                  ...all,
                  { type: 'supporters', value: '', title: '', description: '' },
                ])
              }
            >
              {p('addMilestone')}
            </Button>
            <Button
              size="sm"
              color="primary"
              className="ml-2"
              disabled={!isValid}
              onClick={save}
            >
              {s('save')}
            </Button>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default PlanMilestonesPage;
